import { z } from 'zod';
import { PersonRef } from './people.js';
import { DesignScreen, DesignSetDetail, DesignVersion, DesignSetKind } from './showcase.js';
/**
 * Chapsi Showcase phase D (docs/adr/0069): versions (SC-21), compare (SC-10), approvals (SC-09) and Chapi's gap tips.
 * Every route needs the `showcase` feature; Chapi's parts also need `chapi` (checked in the services).
 */
export const ScreenChange = z.enum(['same', 'changed', 'new']);
export const SCREEN_CHANGE_LABELS = { same: 'Same', changed: 'Changed', new: 'New' };
export const MAX_VERSION_REASON = 500;
export const MAX_APPROVAL_NOTES = 2000;
const Reason = z.string().trim().min(1, 'Say why this version is needed').max(MAX_VERSION_REASON);
export const DesignVersionItem = z.object({
    id: z.uuid(),
    number: z.number().int(),
    createdAt: z.string(),
    createdBy: PersonRef.nullable(),
    publishedAt: z.string().nullable(),
    lockedAt: z.string().nullable(),
    /** Why it was uploaded after an approval (team only). */
    reason: z.string().nullable(),
    status: z.enum(['draft', 'published', 'approved']),
    screenCount: z.number().int(),
    approval: z.object({ name: z.string(), at: z.string() }).nullable(),
});
export const DesignVersionList = z.object({ items: z.array(DesignVersionItem), currentVersion: z.number().int(), publishedVersion: z.number().int() });
export const CreateDesignVersionInput = z.object({
    /** Needed when the latest version is approved: a new version starts a new approval. */
    reason: Reason.optional(),
});
export const PublishDesignVersionInput = z.object({
    reason: Reason.optional(),
    /** Email the clients the set is shared with that the version is ready (on by default). */
    notify: z.boolean().default(true),
    /** A short note for the email (plain text). */
    message: z.string().trim().max(2000).optional(),
});
export const DesignVersionChangeItem = z.object({
    screenId: z.uuid(),
    title: z.string(),
    change: ScreenChange,
    previousScreenId: z.uuid().nullable(),
    previousTitle: z.string().nullable(),
    thumbUrl: z.string().nullable(),
    previousThumbUrl: z.string().nullable(),
    /** Hotspots with a destination on this screen, and how many of them Chapi asks you to check. */
    links: z.number().int(),
    linksToCheck: z.number().int(),
});
export const DesignVersionChanges = z.object({
    versionId: z.uuid(),
    number: z.number().int(),
    previousNumber: z.number().int().nullable(),
    published: z.boolean(),
    /** Screens still uploading or being read: the list will grow. */
    pending: z.number().int(),
    items: z.array(DesignVersionChangeItem),
    removed: z.array(z.object({ screenId: z.uuid(), title: z.string(), thumbUrl: z.string().nullable() })),
    counts: z.object({ same: z.number().int(), changed: z.number().int(), added: z.number().int(), removed: z.number().int() }),
    /** Links carried over from the previous version that are still in place, out of how many there were. */
    linksKept: z.number().int(),
    linksBefore: z.number().int(),
    /** Comments marked Done on earlier versions: clients are asked to check them in this one. */
    doneComments: z.array(z.object({ id: z.uuid(), number: z.number().int(), screenTitle: z.string(), authorName: z.string().nullable() })),
    /** Open comments from earlier versions shown on this one ("from v3"). */
    carriedComments: z.number().int(),
    /** The clients who would be emailed on publish (first names). */
    notifyNames: z.array(z.string()),
    /** The previous version is approved: publishing needs a reason. */
    needsReason: z.boolean(),
    reason: z.string().nullable(),
    /** Chapi's "what changed" (once published, when Chapi is on), else null. */
    summary: z.string().nullable(),
    urlsExpireAt: z.string(),
});
/** Omit `to` for the latest version and `from` for the one before it. */
export const CompareDesignVersionsQuery = z.object({ from: z.coerce.number().int().min(1).max(10_000).optional(), to: z.coerce.number().int().min(1).max(10_000).optional() });
export const DesignComparePair = z.object({
    change: ScreenChange.or(z.literal('removed')),
    title: z.string(),
    before: DesignScreen.nullable(),
    after: DesignScreen.nullable(),
});
export const DesignCompare = z.object({
    from: DesignVersion,
    to: DesignVersion,
    kind: DesignSetKind,
    deviceWidth: z.number().int(),
    deviceHeight: z.number().int(),
    pairs: z.array(DesignComparePair),
    counts: z.object({ same: z.number().int(), changed: z.number().int(), added: z.number().int(), removed: z.number().int() }),
    /** Chapi's change summary for these two versions (adjacent, published, Chapi on); null shows the list instead. */
    summary: z.string().nullable(),
    carriedComments: z.number().int(),
    urlsExpireAt: z.string(),
});
// --- Approvals (SC-09) ----------------------------------------------------------------------------------------
export const ApproveDesignsInput = z
    .object({
    /** The version the person is looking at: only the latest (published) version can be approved. */
    versionNumber: z.number().int().min(1),
    mode: z.enum(['all', 'notes']).default('all'),
    notes: z.string().trim().max(MAX_APPROVAL_NOTES).optional(),
    /** The name they type as their sign-off. */
    name: z.string().trim().min(1, 'Type your name').max(120),
    /** "I approve version N of <set>". */
    confirm: z.literal(true, { error: 'Tick the box to approve' }),
    /** Only when the agency allows approving single screens; omit to approve every screen. */
    screenIds: z.array(z.uuid()).min(1).max(200).optional(),
})
    .refine((v) => v.mode !== 'notes' || !!v.notes, { message: 'Write the changes you would like', path: ['notes'] });
export const DesignApproval = z.object({
    id: z.uuid(),
    versionNumber: z.number().int(),
    name: z.string(),
    email: z.string(),
    approver: PersonRef.nullable(),
    byClient: z.boolean(),
    scope: z.enum(['all', 'screens']),
    screens: z.array(z.object({ id: z.uuid(), title: z.string() })),
    notes: z.string().nullable(),
    createdAt: z.string(),
    /** The address it was sent from: the team only. */
    ip: z.string().nullable(),
    /** The approval certificate (a 5-minute signed link), for the team and the person who approved. */
    certificateUrl: z.string().nullable(),
});
export const DesignApprovalList = z.object({
    items: z.array(DesignApproval),
    /** The reader may approve the latest version now. */
    canApprove: z.boolean(),
    perScreen: z.boolean(),
    /** The latest version clients see, whether it is locked, and its screens already approved one by one. */
    versionNumber: z.number().int(),
    locked: z.boolean(),
    approvedScreenIds: z.array(z.uuid()),
    urlsExpireAt: z.string(),
});
// --- Gap tips (SC-21) ----------------------------------------------------------------------------------------
export const DesignGap = z.object({
    key: z.string(),
    kind: z.enum(['missing_screen', 'tab', 'button']),
    screenId: z.uuid().nullable(),
    screenTitle: z.string().nullable(),
    /** The placeholder's name: "Forgot password". */
    title: z.string(),
    message: z.string(),
    thumbUrl: z.string().nullable(),
    box: z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() }).nullable(),
});
export const DesignGapList = z.object({ items: z.array(DesignGap), urlsExpireAt: z.string() });
export const ActOnDesignGapInput = z.object({ key: z.string().min(1).max(200), action: z.enum(['placeholder', 'ignore', 'hide']) });
// --- Routes ---------------------------------------------------------------------------------------------------
const def = (r) => r;
const id = (name) => z.object({ [name]: z.uuid() });
export const showcaseVersionRoutes = {
    /** Every version of a set, newest first. Clients see published versions only. */
    listDesignVersions: def({ showcaseGuest: true,
        method: 'GET', path: '/v1/showcase/sets/:setId/versions', params: id('setId'), response: DesignVersionList,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
    }),
    /**
     * Starts version N+1 as a draft: every screen of version N carried over (Same, links kept); uploads then replace the
     * screens they match. After an approval it needs a reason.
     */
    createDesignVersion: def({
        method: 'POST', path: '/v1/showcase/sets/:setId/versions', params: id('setId'), body: CreateDesignVersionInput, response: DesignSetDetail,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase', exampleBody: { reason: 'Pack' },
    }),
    /** How the version's screens matched the previous version, links kept, comments to carry and Done ones to check. */
    getDesignVersionChanges: def({
        method: 'GET', path: '/v1/showcase/versions/:versionId/changes', params: id('versionId'), response: DesignVersionChanges,
        isolation: { kind: 'resource', param: 'versionId', resource: 'design_version' }, feature: 'showcase',
    }),
    /** Publishes a draft version: clients now see it (the share link too), with an optional "New version ready" email. */
    publishDesignVersion: def({ rateLimit: 'chapi',
        method: 'POST', path: '/v1/showcase/versions/:versionId/publish', params: id('versionId'), body: PublishDesignVersionInput, response: DesignSetDetail,
        isolation: { kind: 'resource', param: 'versionId', resource: 'design_version' }, feature: 'showcase', exampleBody: { notify: false },
    }),
    /** Two versions side by side: screen pairs with Same, Changed, New and Removed, and Chapi's change summary. */
    compareDesignVersions: def({
        method: 'GET', path: '/v1/showcase/sets/:setId/compare', params: id('setId'), query: CompareDesignVersionsQuery, response: DesignCompare,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
    }),
    /** Approve designs (SC-09): the latest version, by someone with Can approve or the team. Recorded, locked, certified. */
    approveDesignVersion: def({ showcaseGuest: true, rateLimit: 'showcase_comments',
        method: 'POST', path: '/v1/showcase/sets/:setId/approvals', params: id('setId'), body: ApproveDesignsInput, response: DesignApprovalList,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
        exampleBody: { versionNumber: 1, name: 'Pack Person', confirm: true },
    }),
    /** The set's approval records. */
    listDesignApprovals: def({ showcaseGuest: true,
        method: 'GET', path: '/v1/showcase/sets/:setId/approvals', params: id('setId'), response: DesignApprovalList,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
    }),
    /** Chapi's "Looks missing from the flow" tips for the latest version (Chapi on). */
    listDesignGaps: def({
        method: 'GET', path: '/v1/showcase/sets/:setId/gaps', params: id('setId'), response: DesignGapList,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase',
    }),
    /** Add placeholder, Ignore, or Hide this tip. */
    actOnDesignGap: def({
        method: 'POST', path: '/v1/showcase/sets/:setId/gaps', params: id('setId'), body: ActOnDesignGapInput, response: DesignGapList,
        isolation: { kind: 'resource', param: 'setId', resource: 'design_set' }, feature: 'showcase', exampleBody: { key: 'missing:empty-state', action: 'ignore' },
    }),
};
//# sourceMappingURL=showcase-versions.js.map